import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { addressDetailsQuery, autocompleteAddressQuery } from './queries';
import ToastModule from '@modules/ToastModule';
import { uniqueId } from 'lodash';
import { Address } from '@api/generated/graphql';
import { defaultAddress, FormAddressType } from '../components/AddressForm';

const DEBOUNCE_TIME_MS = 500;
const MIN_SEARCH_LENGTH = 4;
const ERROR_TOAST_DURATION = 5000;

/**
 * Returns an object containing the state and functions related to the suggested address search.
 *
 * This component is mainly responsible for fetching the suggested addresses based on the given
 * "watched" street value and handling the address details query when a suggested address is clicked.
 *
 *
 * @param formAddress The address form values.
 * @returns {Object} An object with the following properties:
 * - `suggestedAddresses` {Array}: An array of suggested addresses based on the search input.
 * - `loadingSuggestedAddresses` {boolean}: A boolean indicating whether the suggested addresses are currently being loaded.
 * - `errorSuggestedAddresses` {Error | null}: An error object if there was an error loading the suggested addresses, otherwise null.
 * - `fetchAddressDetails` {Function}: A function that returns a Promise which resolves with the address details when the query is completed. This should be called when a suggested address is clicked
 */
const useSuggestedAddressSearch = (
  formAddress: FormAddressType = defaultAddress,
) => {
  const [sessionToken, setSessionToken] = useState<string>(uniqueId());

  const { street, unit, city, state, country, zip } = formAddress;

  // Query to fetch the suggested addresses based on the search input
  const [
    fetchSuggestedAddresses,
    {
      data: fetchSuggestedAddressData,
      loading: loadingSuggestedAddresses,
      error: errorSuggestedAddresses,
    },
  ] = useLazyQuery(autocompleteAddressQuery, {
    onError: (error) => {
      ToastModule.showToast({
        title: error.message,
        status: 'warning',
        duration: ERROR_TOAST_DURATION,
      });
    },
  });

  // Query to fetch the address details when a suggested address is clicked
  const [fetchAddressDetails] = useLazyQuery(addressDetailsQuery);

  /**
   * Returns a Promise which resolves with the address details when the query is completed.
   *
   * @param placeId placeId of the suggested address, coming from google api
   * @returns A Promise that resolves with the address details.
   */
  const fetchSuggestedAddressDetails = (placeId: string): Promise<Address> => {
    return new Promise((resolve, reject) => {
      fetchAddressDetails({
        variables: {
          placeId, // UI has single search input for street, unit, city, state, country, zip
          sessionToken, // random unique id for each google api session to keep the costs low
        },
      })
        .then((response) => {
          if (response.data) {
            resolve(response.data.addressDetails);
          } else {
            reject(new Error('Address details not found'));
          }
        })
        .catch((error) => {
          ToastModule.showToast({
            title: error.message,
            status: 'warning',
            duration: ERROR_TOAST_DURATION,
          });
          reject(new Error('Cannot get address details. ' + error.message));
        })
        .finally(() => {
          setSessionToken(uniqueId()); // reset the session token after each address details query
        });
    });
  };

  const searchSuggestedAddresses = useCallback(() => {
    fetchSuggestedAddresses({
      variables: {
        input: street, // UI has single search input for street, unit, city, state, country, zip
        sessionToken, // random unique id for each google api session to keep the costs low
      },
    });
  }, [fetchSuggestedAddresses, street, sessionToken]);

  // Handles debouncing the search input. This is to prevent the search from being called on every keystroke.
  useEffect(() => {
    const handler = setTimeout(() => {
      if (street.length >= MIN_SEARCH_LENGTH) {
        searchSuggestedAddresses();
      }
    }, DEBOUNCE_TIME_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [street, unit, city, state, country, zip, searchSuggestedAddresses]);

  return {
    suggestedAddresses:
      street.length >= MIN_SEARCH_LENGTH
        ? fetchSuggestedAddressData?.autocompleteAddress.suggestions ?? []
        : [],
    loadingSuggestedAddresses: loadingSuggestedAddresses,
    errorSuggestedAddresses,
    fetchSuggestedAddressDetails,
  };
};

export default useSuggestedAddressSearch;
